import React, { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import LabelBankLabel from './LabelBankLabel';
import { isWithinBounds, translatePosition } from '~app/core/positionUtils';
import { actions as CanvasActions } from '~app/features/canvas';

const LabelBank = ({ viewportRef }) => {
  const dispatch = useDispatch();
  const labels = useSelector(state => state.document.present?.labels);
  const labelBankItems =
    useSelector(state => state.document.present?.labelBankItems) ?? [];
  const [isDragging, setIsDragging] = useState(false);
  const [scrollOffset, setScrollOffset] = useState(0);
  const containerRef = useRef();

  // A bit about what's going on here:
  // We want the label bank to be vertically scrollable, so the container has
  // "overflow: auto". However, this makes it so you can't drag the label outside
  // the container; for that you need "overflow: visible". Also, even though there's
  // overflow-x and overflow-y, it doesn't work to use 'visible' for x and 'scroll'
  // or 'auto' for y.
  //
  // So the workaround is to add a 'dragActive' class which changes "overflow: auto"
  // to "overflow: visible" just while dragging, allowing you to drag outside the
  // container. But the new wrinkle is changing overflow to 'visible' will reset the
  // scrolling so the inner object will jump back to the top. To workaround THAT
  // we need to capture the scroll position and then set the top margin to the
  // negative of that when overflow is 'visible', so that the content doesn't change
  // when you start dragging.

  const onDrag = useCallback(() => {
    if (!isDragging) {
      setScrollOffset(containerRef.current.scrollTop);
      setIsDragging(true);
    }
  }, [isDragging, setIsDragging, setScrollOffset, containerRef]);

  const onStop = useCallback(
    (e, data) => {
      setIsDragging(false);
      setScrollOffset(0);
      if (isWithinBounds(data.node, viewportRef.current)) {
        let { x, y, width, height } = translatePosition(
          data.node,
          viewportRef.current,
        );
        // the canvas uses the center of the objects for positioning instead of the corner
        x += width * 0.5;
        y += height * 0.5;
        dispatch(
          CanvasActions.dropLabel({ label: data.node.dataset.label, x, y }),
        );
      }
    },
    [setIsDragging, setScrollOffset, dispatch, viewportRef],
  );

  const handleDropLabel = useCallback(
    e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        dispatch(
          CanvasActions.dropLabel({ label: e.currentTarget.dataset.label }),
        );
      }
    },
    [dispatch],
  );

  return (
    <div
      id="label-bank-container"
      ref={containerRef}
      className={classNames('LabelBank__container', {
        'LabelBank__container--dragActive': isDragging,
      })}
    >
      <div
        className="LabelBank__background"
        style={{ marginTop: -scrollOffset }}
        role="list"
      >
        {labelBankItems.map(labelBankItem => (
          <LabelBankLabel
            key={labelBankItem}
            label={labels[labelBankItem]}
            grabbed={isDragging}
            draggableProps={{ position: { x: 0, y: 0 }, onDrag, onStop }}
            divProps={{
              'data-type': 'node',
              'data-label': labelBankItem,
              onKeyPress: handleDropLabel,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default LabelBank;
